import React from 'react';
import { lightTheme, MODULE_THEMES } from '../../Theme';
import { Box, Stack } from '../../ui';
import { Module } from '../../Module';
import { TemplateContentType } from '../index';
import { HelpHeader } from '../../Help/HelpHeader';
import { HelpFAQ } from '../../Help/HelpFAQ';
import { TopicGrid } from '../../Help/TopicGrid';
import { kleinBlue } from '@spotify-internal/encore-web';
import { ThemeProvider } from 'styled-components';
import { useDynamicNavBarColor } from '../useDynamicNavBarColor';
import { FaqCategory, FaqArticle } from '../../Help/util';
import { itemToSearchHit } from '../../Search';
import type { SearchHit } from '../../Search/types';
import { useLocale, useT } from '@spotify-internal/i18n-core';
import { ModuleType } from '../../Module/types';

export interface HelpHomeTemplateType {
  id: string;
  contentType: TemplateContentType;
  headline?: string;
  frequentlyAskedQuestions?: FaqArticle[];
  extraCategories?: FaqCategory[];
  faqCategories?: FaqCategory[];
  modules?: ModuleType[];
}

export const HelpHomeTemplate: React.FC<HelpHomeTemplateType> = template => {
  const t = useT();
  const locale = useLocale();
  const {
    headline = t('FRODOR_bddb15', 'Creator Marketing Support', ''),
    frequentlyAskedQuestions,
    faqCategories,
    modules,
  } = template;

  const allModules: any = [...(modules || [])].filter(m =>
    Boolean(m?.contentType),
  );

  useDynamicNavBarColor({ backgroundColor: kleinBlue });

  const renderModule = React.useCallback((mod: ModuleType) => {
    const modProps = { ...mod } as any;
    return (
      <div>
        <Module {...modProps} />
      </div>
    );
  }, []);

  const modTheme = (mod: ModuleType) => ({
    ...lightTheme,
    ...(MODULE_THEMES[mod.themeName || ''] || {}),
  });

  const faqs = frequentlyAskedQuestions
    ?.map(item => itemToSearchHit(item, locale))
    .filter(Boolean) as NonNullable<SearchHit>[];

  return (
    <ThemeProvider theme={lightTheme}>
      <Stack data-testid="help-home-help">
        <HelpHeader headline={headline} backgroundColor={kleinBlue} />
        {faqs && <HelpFAQ items={faqs} />}
        {faqCategories && <TopicGrid items={faqCategories} />}
        {allModules.map((mod: ModuleType, idx: number) => (
          <ThemeProvider key={`${mod.id}-${idx}`} theme={modTheme(mod)}>
            <Box>{renderModule(mod)}</Box>
          </ThemeProvider>
        ))}
      </Stack>
    </ThemeProvider>
  );
};
